import type { FC } from 'react'
import { useMemo, useState } from 'react'

import { Container } from '@skytech/ui/Container'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import { leasingResources } from '@/resources/leasing'

import { EndOfLeaseWarning } from './EndOfLease/EndOfLeaseWarning'
import { getEndOfLeaseNearbyDateStatus } from './EndOfLease/helper'
import { Form } from './Form'
import { InfoCard } from './InfoCard'

import './index.styles.scss'

export const AssetDetails: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const [userTypeId, setUserTypeId] = useState<number | null>(null)
  const params = useParams()
  const assetId = Number(params.id)

  const { data: asset } = useQuery(queries.adminAsset(assetId), () =>
    adminResources.getAsset(assetId)
  )

  const { data: leasingData } = useQuery(
    queries.adminLeasingAssetDetails(assetId),
    () => leasingResources.getLeasingAssetDetails(assetId),
    { enabled: !!asset?.isLeased }
  )

  if (isNaN(assetId)) throw Error(translate('Asset ID is not a number!'))

  const endOfLeaseNearby = useMemo(
    () => getEndOfLeaseNearbyDateStatus(asset, leasingData),
    [asset, leasingData]
  )

  return (
    <Container onSwipedLeft={() => navigate('/admin/assets')} className="asset-details">
      <Container.Header>
        <Translation id="Asset details" /> ({assetId})
      </Container.Header>

      {endOfLeaseNearby && <EndOfLeaseWarning />}

      <InfoCard userTypeId={userTypeId || asset?.userTypeId || null} />

      <section className="asset-details-content">
        <Form
          assetId={assetId}
          onChangeUserTypeId={(_userTypeId: number) => setUserTypeId(_userTypeId)}
        />
      </section>
    </Container>
  )
}
